import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Home, 
  Boxes, 
  Plus,
  Menu,
  Search,
  X,
  ChevronDown,
  Settings,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

import Profile from './Profile';
import {searchByName} from "../api/api";
import config from '../utils/config';

const StickyNavbar = ({ sidebarOpen, setSidebarOpen }) => {
  const searchContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  const handleGoBack = () => {
    navigate(-1);
  };

  // Check if route is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Animation variants for the toggle button
  const toggleVariants = {
    open: { scale: 1 },
    closed: { scale: 1 },
    hover: { scale: 1.05 }
  };

  // Handle scroll effect
  useEffect(() => {

    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchExpanded(false);  // Shrink search bar
        setShowResults(false);     // Also hide results
      }
    };

    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle search functionality
  const handleSearchChange = async (e) => {
    const query = e.target.value;
    setSearchText(query);

    
    if (query.trim().length > 1) {
      setIsSearching(true);
      setShowResults(true);
      
      try {
        // Call the searchByName API
        const response = await searchByName(query);

        if (response.length == 0) {
          throw new Error('Search request failed');
        }
        //alert(JSON.stringify(response));

        setSearchResults(response.tools);
      } catch (error) {
        console.error('Error searching tools:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  // Handle clicking outside to close results
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.getElementById('search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems = [
    { icon: Boxes, label: 'Tool Groups', path: '/catgs' },
    { icon: Plus, label: 'Suggest Tool', path: '/suggest' },
  ];

  const handleResultClick = (tool) => {
    //navigate(`/tool/${toolId}`);
    navigate("/tool", { state: { toolcard: tool } });
    setShowResults(false);
    setSearchText("");
  };

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}>

      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Left Section: Sidebar Toggle + Logo */}
          <div className="flex items-center space-x-3">

            {/* Sidebar Toggle Button */}
            {setSidebarOpen && (
              <motion.button 
                className={`p-2 rounded-lg transition-colors duration-300 shadow-sm border
                  ${sidebarOpen 
                    ? 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100' 
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                  }`}
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
                variants={toggleVariants}
                initial={sidebarOpen ? "open" : "closed"}
                animate={sidebarOpen ? "open" : "closed"}
                whileHover="hover"
                title={sidebarOpen ? 'Hide history' : 'Show history'}
              >
                {sidebarOpen ? (
                  <Menu className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </motion.button>
            )}
            
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <img 
                src="/tuly.png"
                alt="Tuly Logo" 
                className="h-12 w-auto transition-transform group-hover:scale-105"
              />
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link 
                key={item.path} 
                to={item.path} 
                className={`flex items-center px-3 py-2 rounded-md transition-all ${
                  isActive(item.path) 
                    ? 'text-blue-600 bg-blue-50 font-medium' 
                    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                }`}>
                <item.icon className={`h-4 w-4 ${isActive(item.path) ? 'mr-2' : 'mr-1.5'}`} />
                {item.label}
              </Link>
            ))}
          </div>

          {/* Right Section: Search and Profile */}
          <div className="flex items-center space-x-2">
            {/* Search Component */}
            <div id="search-container" className="relative hidden sm:block">
              <div className={`flex items-center transition-all duration-300 ${
                searchExpanded ? 'w-64' : 'w-40'}`}>
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search tool"
                    value={searchText}
                    onChange={handleSearchChange}
                    onFocus={() => {
                      setSearchExpanded(true);
                      if (searchText.trim().length > 1) {
                        setShowResults(true);
                      }
                    }}
                    onBlur={() => {
                      // Optional: Add a small delay to allow clicking on results
                      setTimeout(() => {
                        if (!searchContainerRef.current?.contains(document.activeElement)) {
                          setSearchExpanded(false);
                          setShowResults(false);
                        }
                      }, 150);
                    }}
                    className="w-full pl-9 pr-3 py-1.5 rounded-full border border-gray-200 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 text-sm"
                  />
                  <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
              </div>
              
              {/* Search Results Dropdown */}
              {showResults && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-y-auto z-50">
                  {isSearching ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      Searching...
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div>
                      {searchResults.map((tool) => (
                        <div 
                          key={tool.id} 
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center"
                          onClick={() => handleResultClick(tool)}
                        >
                          {tool.logo && (
                            <img 
                              src={`${config.IMG_URL}/images/tools/${tool.logo}`} 
                              alt={`${tool.name} logo`} 
                              className="h-6 w-6 mr-3 object-contain"
                            />
                          )}
                          <span className="text-sm">{tool.name}</span>
                        </div>
                      ))}
                    </div>
                  ) : searchText.trim().length > 1 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      No tools found
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Profile Component with slight margin */}
            <div className="ml-2">
              <Profile />
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden flex items-center justify-center h-8 w-8 rounded-md text-gray-600 hover:bg-gray-100"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu (Slide Down) */}
      <div 
        className={`md:hidden bg-white shadow-md overflow-hidden transition-all duration-300 ${
          isMenuOpen ? 'max-h-64' : 'max-h-0'
        }`}
      >
        {/* Mobile Sidebar Toggle */}
        {setSidebarOpen && (
          <div className="px-4 pt-2">
            <button 
              className={`w-full flex items-center justify-center px-3 py-2 rounded-md transition-all ${
                sidebarOpen 
                  ? 'text-blue-600 bg-blue-50 border border-blue-200' 
                  : 'text-gray-600 bg-gray-50 border border-gray-200'
              }`}
              onClick={() => {
                setSidebarOpen(!sidebarOpen);
                setIsMenuOpen(false);
              }}
            >
              {sidebarOpen ? (
                <>
                  <ChevronLeft className="h-5 w-5 mr-2" />
                  Hide History
                </>
              ) : (
                <>
                  <ChevronRight className="h-5 w-5 mr-2" />
                  Show History
                </>
              )}
            </button>
          </div>
        )}

        {/* Mobile Search Bar */}
        <div className="px-4 pt-2 pb-1">
          <div className="relative">
            <input
              type="text"
              placeholder="Search tools..."
              value={searchText}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-200 text-sm"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            
            {/* Mobile Search Results */}
            {showResults && searchText.trim().length > 1 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-48 overflow-y-auto z-50">
                {isSearching ? (
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">
                    Searching...
                  </div>
                ) : searchResults.length > 0 ? (
                  <div>
                    {searchResults.map((tool) => (
                      <div 
                        key={tool.id} 
                        className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center"
                        onClick={() => {
                          handleResultClick(tool.id);
                          setIsMenuOpen(false);
                        }}
                      >
                        {tool.logo && (
                          <img 
                            src={tool.logo} 
                            alt={`${tool.name} logo`} 
                            className="h-6 w-6 mr-3 object-contain"
                          />
                        )}
                        <span className="text-sm">{tool.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">
                    No tools found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="px-4 py-2 space-y-1">
          {navItems.map((item) => (
            <Link 
              key={item.path} 
              to={item.path} 
              className={`flex items-center px-3 py-2 rounded-md transition-all ${
                isActive(item.path) 
                  ? 'text-blue-600 bg-blue-50 font-medium' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              <item.icon className="h-5 w-5 mr-3" />
              {item.label}
            </Link>
          ))}
          
          {/* Settings Menu Item */}
          <Link 
            to="/settings" 
            className="flex items-center px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50"
            onClick={() => setIsMenuOpen(false)}
          >
            <Settings className="h-5 w-5 mr-3" />
            Settings
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default StickyNavbar;